import {
  AGENT_HUB_AUTH_ROLE_AFFILIATION_ADMIN,
  AGENT_HUB_AUTH_ROLE_AGENCY_ADMIN,
  COMMISSION_RULE_CONDITION_TYPES,
  ruleConditionsToTextMap,
} from '~/consts/agentHub';
import type { CommissionOption, CommissionsConditionTypes } from '~/consts/agentHub';

import type { Agent } from '~/types/services/agentHub';

export const INDEPENDENT_AGENT_HUB_AFFILIATION_KEYWORD = 'independent';

type AgentMaybeRoles = Agent & { roles?: Array<string> };
type CommissionConditionOperator = 'eq' | 'gt' | 'lt' | 'gte' | 'lte' | 'neq';
type CommissionGroupOperator = 'AND' | 'OR';
type CommissionConditionFormLike = {
  type: CommissionsConditionTypes;
  operator: CommissionConditionOperator;
  value: CommissionOption;
};
type CommissionGroupFormLike = {
  operator: CommissionGroupOperator;
  conditions: Array<CommissionConditionFormLike>;
};
type CommissionGroupConditionPayload = {
  operator: CommissionConditionOperator;
  value: string;
  type: string;
};
type CommissionGroupPayload = {
  operator: CommissionGroupOperator;
  conditions: Array<CommissionGroupConditionPayload>;
};

const CONDITION_OPERATOR_SYMBOL: Record<CommissionConditionOperator, string> = {
  eq: '=',
  neq: '!=',
  gt: '>',
  gte: '>=',
  lt: '<',
  lte: '<=',
};

export function isIndependentAgentHubAffiliation(user: Pick<Agent, 'affiliation'> | undefined): boolean {
  return user?.affiliation?.trim().toLowerCase().includes(INDEPENDENT_AGENT_HUB_AFFILIATION_KEYWORD) ?? false;
}

export function resolveAgentHubAdminDisplayFlags(user: Agent | undefined): {
  isAgencyAdmin: boolean;
  isAffiliationAdmin: boolean;
} {
  if (!user) {
    return { isAgencyAdmin: false, isAffiliationAdmin: false };
  }

  const roles = (user as AgentMaybeRoles).roles;
  if (Array.isArray(roles) && roles.length > 0) {
    return {
      isAgencyAdmin: roles.includes(AGENT_HUB_AUTH_ROLE_AGENCY_ADMIN),
      isAffiliationAdmin: roles.includes(AGENT_HUB_AUTH_ROLE_AFFILIATION_ADMIN),
    };
  }

  return {
    isAgencyAdmin: user.isAgencyAdmin === true,
    isAffiliationAdmin: user.isAffiliationAdmin === true,
  };
}

export function areCommissionOptionsEqual(left: CommissionOption = [], right: CommissionOption = []) {
  return (
    left.length === right.length &&
    left.every((option, index) => option.value === right[index]?.value && option.label === right[index]?.label)
  );
}

export function normalizeApiConditionType(type: string): CommissionsConditionTypes | undefined {
  return COMMISSION_RULE_CONDITION_TYPES.includes(type as CommissionsConditionTypes)
    ? (type as CommissionsConditionTypes)
    : undefined;
}

function buildConditionPayload(condition: CommissionConditionFormLike): Array<CommissionGroupConditionPayload> {
  return condition.value.map((option) => ({
    type: condition.type,
    operator: condition.operator,
    value: String(option.value),
  }));
}

export function buildCommissionGroupPayload(groups: Array<CommissionGroupFormLike>): Array<CommissionGroupPayload> {
  return groups
    .map((group) => ({
      operator: group.operator,
      conditions: group.conditions.flatMap(buildConditionPayload),
    }))
    .filter((group) => group.conditions.length > 0);
}

function joinWords(items: Array<string>, connector: string) {
  if (items.length <= 1) {
    return items[0] ?? '';
  }
  return `${items.slice(0, -1).join(` ${connector} `)} ${connector} ${items[items.length - 1]}`;
}

function getConditionBreakdownLabel(type: CommissionsConditionTypes) {
  return (ruleConditionsToTextMap.get(type) ?? type).toLowerCase();
}

function getConditionBreakdownValue(option: CommissionOption[number], type: CommissionsConditionTypes) {
  if (['margin', 'bundleTotalPrice', 'minTotalSpend'].includes(type)) {
    return String(option.value);
  }
  return String(option.label || option.value);
}

function buildConditionTypeBreakdown(
  type: CommissionsConditionTypes,
  conditions: Array<CommissionConditionFormLike>,
  groupOperator: CommissionGroupOperator,
) {
  const connector = groupOperator.toLowerCase();
  const label = getConditionBreakdownLabel(type);
  const atoms = conditions.flatMap((condition) =>
    condition.value.map((option) => ({
      operator: condition.operator,
      value: getConditionBreakdownValue(option, condition.type),
    })),
  );

  if (atoms.length === 0) {
    return '';
  }

  const firstOperator = atoms[0].operator;
  const usesOneOperator = atoms.every((atom) => atom.operator === firstOperator);

  if (usesOneOperator) {
    return `${label} must ${CONDITION_OPERATOR_SYMBOL[firstOperator]} ${joinWords(
      atoms.map((atom) => atom.value),
      connector,
    )}`;
  }

  return `${label} must ${joinWords(
    atoms.map((atom) => `${CONDITION_OPERATOR_SYMBOL[atom.operator]} ${atom.value}`),
    connector,
  )}`;
}

function buildGroupBreakdown(group: CommissionGroupFormLike) {
  const conditionsByType = group.conditions.reduce((acc, condition) => {
    if (condition.value.length === 0) {
      return acc;
    }
    const conditions = acc.get(condition.type) ?? [];
    conditions.push(condition);
    acc.set(condition.type, conditions);
    return acc;
  }, new Map<CommissionsConditionTypes, Array<CommissionConditionFormLike>>());

  return joinWords(
    Array.from(conditionsByType.entries())
      .map(([type, conditions]) => buildConditionTypeBreakdown(type, conditions, group.operator))
      .filter(Boolean),
    group.operator.toLowerCase(),
  );
}

export function buildCommissionConditionBreakdown(groups: Array<CommissionGroupFormLike>) {
  return groups.map(buildGroupBreakdown).filter(Boolean);
}
